import i18next from 'i18next'
import * as React from 'react'
import { InView } from 'react-intersection-observer'
import {
  AuthButtonPrimary,
  FormControls,
  FormNotification,
  formSkeletonConfig,
} from '../../../utils/forms'
import { blockInviewHandler } from '../../../utils/inview'
import { PageHeaderLayout } from '../pageHeader/pageHeaderLayout'
import { ContactFormInput, contactFormInputs } from './contactState'

type ContactLayoutProps = {
  formInput: Partial<{
    [field in ContactFormInput]: string
  }>
  onChange: {
    [field in ContactFormInput]: (newValue: string) => void
  }
  showFormInputValidation: {
    [field in ContactFormInput]: boolean
  }
  onBlur: {
    [field in ContactFormInput]: () => void
  }
  isValid: {
    [field in ContactFormInput]: boolean
  }
  onSubmit: () => void
  submitStatus: 'not submitted' | 'submitting' | 'success' | 'error'
}

export const ContactLayout = (props: ContactLayoutProps) => {
  return (
    <>
      <PageHeaderLayout
        kind="knowledge"
        title={i18next.t('contact:pageHeaderTitle')}
        intro={i18next.t('contact:pageHeaderIntro')}
      />
      <InView
        as="section"
        threshold={0}
        triggerOnce={true}
        onChange={(isInView, entry) =>
          blockInviewHandler(isInView, entry.target)
        }
        className="block block--contact block--text block--wysiwyg"
      >
        <div className="block__inner container--m gutter--m">
          <div className="block__content divide-children">
            <section>
              <address className="spacing--wysiwyg">
                <p>
                  <b>{i18next.t('invoices:AboutUsOwner')}</b>{' '}
                  {i18next.t('invoices:AboutUsOwnerSuffix')}
                </p>
                <p>{i18next.t('invoices:AboutUsAddress')}</p>
                <p>
                  {i18next.t('invoices:AboutUsPostcode')}{' '}
                  {i18next.t('invoices:AboutUsCity')}
                </p>
                <p>{i18next.t('invoices:AboutUsCountry')}</p>
                <p>{i18next.t('invoices:AboutUsEmail')}</p>
                <p>{i18next.t('invoices:AboutUsPhone')}</p>
                <p>{i18next.t('invoices:AboutUsCoC')}</p>
                <p>{i18next.t('invoices:AboutUsVATNL')}</p>
                <p>{i18next.t('invoices:AboutUsVATGB')}</p>
              </address>
            </section>

            <section>
              <header className="block__header lf__subheader">
                <h2 className="block__title title--s">
                  {i18next.t('contact:formTitle')}
                </h2>
              </header>

              {props.submitStatus === 'success' ? (
                <FormNotification
                  status="success"
                  options={{
                    style: formSkeletonConfig.options.style,
                  }}
                  message={i18next.t('contact:submitSuccess')}
                />
              ) : (
                <>
                  {props.submitStatus === 'error' && (
                    <FormNotification
                      options={{
                        style: formSkeletonConfig.options.style,
                      }}
                      message={i18next.t('contact:submitFailed')}
                    />
                  )}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault(), props.onSubmit()
                    }}
                  >
                    <FormControls.FormItemInGroup
                      controlId="contactEmail"
                      label={i18next.t('auth:email')}
                      type="email"
                      readOnly={props.submitStatus === 'submitting'}
                      required={true}
                      inputMode="email"
                      value={props.formInput.email}
                      onChange={props.onChange.email}
                      onBlur={props.onBlur.email}
                      validation={
                        props.showFormInputValidation.email &&
                        !props.isValid.email
                          ? {
                              kind: 'failed',
                              message: i18next.t(
                                'auth:signupEmailError'
                              ),
                            }
                          : undefined
                      }
                    />
                    <FormControls.FormItemInGroup
                      controlId="firstName"
                      label={i18next.t('auth:firstName')}
                      type="text"
                      required={true}
                      readOnly={props.submitStatus === 'submitting'}
                      value={props.formInput.name}
                      onChange={props.onChange.name}
                      onBlur={props.onBlur.name}
                      validation={
                        props.showFormInputValidation.name &&
                        !props.isValid.name
                          ? {
                              kind: 'failed',
                              message: i18next.t(
                                'auth:signupNameError'
                              ),
                            }
                          : undefined
                      }
                    />
                    <FormControls.FormItemInGroup
                      controlId="lastName"
                      label={i18next.t('auth:surname')}
                      type="text"
                      required={true}
                      readOnly={props.submitStatus === 'submitting'}
                      value={props.formInput.surname}
                      onChange={props.onChange.surname}
                      onBlur={props.onBlur.surname}
                      validation={
                        props.showFormInputValidation.surname &&
                        !props.isValid.surname
                          ? {
                              kind: 'failed',
                              message: i18next.t(
                                'auth:signupSurnameError'
                              ),
                            }
                          : undefined
                      }
                    />
                    <FormControls.FormItemInGroup
                      controlId="contactSubject"
                      label={i18next.t('auth:subject')}
                      type="text"
                      required={true}
                      readOnly={props.submitStatus === 'submitting'}
                      value={props.formInput.subject}
                      onChange={props.onChange.subject}
                      onBlur={props.onBlur.subject}
                      validation={
                        props.showFormInputValidation.subject &&
                        !props.isValid.subject
                          ? {
                              kind: 'failed',
                              message: i18next.t('auth:subjectError'),
                            }
                          : undefined
                      }
                    />
                    <FormControls.FormItemInGroup
                      as="textarea"
                      rows={6}
                      controlId="contactMessage"
                      label={i18next.t('auth:message')}
                      required={true}
                      readOnly={props.submitStatus === 'submitting'}
                      value={props.formInput.message}
                      onChange={props.onChange.message}
                      onBlur={props.onBlur.message}
                      validation={
                        props.showFormInputValidation.message &&
                        !props.isValid.message
                          ? {
                              kind: 'failed',
                              message: i18next.t('auth:messageError'),
                            }
                          : undefined
                      }
                    />
                    <footer className="block__footer lf__actions">
                      <div className="lf__action">
                        <AuthButtonPrimary
                          kind="button"
                          type="submit"
                          label={
                            props.submitStatus === 'submitting' ? (
                              <FormControls.SubmittingSpinner />
                            ) : (
                              i18next.t('contact:formSubmit')
                            )
                          }
                          disabled={
                            !contactFormInputs.every(
                              (field) => props.isValid[field]
                            ) || props.submitStatus === 'submitting'
                          }
                          onClick={() => {}}
                        />
                      </div>
                    </footer>
                  </form>
                </>
              )}
            </section>
          </div>
        </div>
      </InView>
    </>
  )
}
